import React from 'react';
import { CTA_PREFIXES, trackCtaClick } from '../../hooks/useClickTracking';
import { NavLink } from './MegaMenu';
import { Badge } from '../Badge/Badge';
import { Image } from '../Image/Image';
import type { ColumnVariant } from './PanelLayout';
import './LinkColumn.scss';

/**
 * LinkColumn - Single column of L2 navigation links within a dropdown panel
 *
 * Renders a titled group of navigation links with support for:
 * - Single and dual column layouts (columnType)
 * - Visual variants: with-icons, without-icons, icon-only
 * - Badge text on individual links
 * - Action link at bottom of column with analytics attributes
 *
 * Self-contained subcomponent: receives all data as props, no knowledge of siblings.
 */

export interface LinkColumnProps {
  /** Column heading title */
  title: string;
  /** Array of navigation links in this column */
  links: NavLink[];
  /** Column layout type */
  columnType?: 'single' | 'dual';
  /** Visual variant controlling how links render */
  variant?: ColumnVariant;
  /** Action link text at bottom of column */
  actionText?: string;
  /** Action link URL */
  actionUrl?: string;
  /** Action link CTA ID for analytics */
  actionCtaId?: string;
}

export const LinkColumn: React.FC<LinkColumnProps> = ({
  title,
  links,
  columnType = 'single',
  variant = 'without-icons',
  actionText,
  actionUrl,
  actionCtaId,
}) => {
  const isDual = columnType === 'dual';
  const isIconOnly = variant === 'icon-only';
  const showIcons = variant === 'with-icons' || isIconOnly;
  // Descriptions only render with 'with-icons' variant. The old linkStyle: ['description']
  // opt-in is intentionally collapsed — without-icons columns don't show descriptions.
  const showDescriptions = variant === 'with-icons';

  // In icon-only mode, links without iconUrl would render as invisible focusable <a> tags
  // (no icon, no text, but aria-label present). Filter them out to keep the DOM clean.
  const effectiveLinks = isIconOnly ? links.filter(l => l.iconUrl) : links;
  if (isIconOnly && process.env.NODE_ENV !== 'production' && effectiveLinks.length < links.length) {
    console.warn(`[LinkColumn] ${links.length - effectiveLinks.length} icon-only link(s) have no iconUrl and were removed from the DOM.`);
  }

  const columnClasses = [
    'poly-link-column',
    isDual ? 'poly-link-column--dual' : '',
    isIconOnly ? 'poly-link-column--icon-only' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={columnClasses}>
      <h3 className="poly-link-column__title">{title}</h3>
      <ul className="poly-link-column__list" role="list">
        {effectiveLinks.map((link, index) => (
          <li
            key={link.id || index}
            className={`poly-link-column__item${isDual && link.align === 'right' ? ' poly-link-column__item--right' : ''}`}
          >
            <a
              href={link.url}
              className="poly-link-column__link"
              data-cta-id={link.id || undefined}
              aria-label={isIconOnly ? link.title : undefined}
              onClick={() => trackCtaClick(CTA_PREFIXES.nav, { ctaId: link.id, href: link.url, label: link.title })}
            >

              {showIcons && link.iconUrl && (
                <Image
                  bare
                  url={link.iconUrl}
                  alt={isIconOnly ? '' : (link.iconAlt ?? '')}
                  className="poly-link-column__icon"
                  aria-hidden={isIconOnly || !link.iconAlt ? 'true' : undefined}
                />
              )}
              {!isIconOnly && (
                <span className="poly-link-column__link-content">
                  <span className="poly-link-column__link-title">
                    {link.badgeText && (
                      <Badge size="sm" tone="dark" className="poly-link-column__badge">
                        {link.badgeText}
                      </Badge>
                    )}
                    {link.title}
                  </span>
                  {showDescriptions && link.description && (
                    <span className="poly-link-column__description">{link.description}</span>
                  )}
                </span>
              )}
            </a>
          </li>
        ))}
      </ul>
      {actionText && actionUrl && (
        <a
          href={actionUrl}
          className="poly-link-column__action"
          data-cta-id={actionCtaId || undefined}
          onClick={() => trackCtaClick(CTA_PREFIXES.nav, { ctaId: actionCtaId, href: actionUrl, label: actionText })}
        >
          {actionText}
        </a>
      )}
    </div>
  );
};
